<template>
  <div class="participant-courses--wrap">
    <div class="d-flex justify-end px-6 py-6" v-if="!isCurrentPageRootLevel">
      <patient-courses-filters
        :status="coursesFilters.status"
        @filter-courses="filterCourses"
      />
    </div>
    <div class="participant-courses" v-if="!isCurrentPageRootLevel">
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <div
        class="container"
        v-if="
          coursesWithCompletePercentage && coursesWithCompletePercentage.length
        "
      >
        <v-row>
          <v-col>
            <main-user-table
              v-if="
                coursesWithCompletePercentage &&
                  coursesWithCompletePercentage.length
              "
              :headers="headers"
              :items="coursesWithCompletePercentage"
              :items-per-page="-1"
              :show-all-items="true"
              :server-items-length="-1"
              :page="0"
              :row-component="'participant-courses-table-row'"
              :hide-default-footer="true"
              @to-course="navigateToCourse"
              @unassign="unassignCourse"
              @action:get-data="getDataAction"
            >
            </main-user-table>
          </v-col>
        </v-row>
      </div>
      <v-container v-else>
        <v-row class="fill-height">
          <v-col>
            <h2>No Courses found.</h2>

            <v-btn
              class="rounded-pill mt-2"
              color="accent"
              @click="navigateToCourses"
              v-if="isAdvisor"
              >Assign one for this Participant</v-btn
            >
          </v-col>
        </v-row>
      </v-container>
    </div>
    <router-view v-else />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import RouterMixin from '@/mixins/router'
import MainUserTable from '@/components/tables/MainUserTable.vue'
import PatientCoursesFilters from '@/components/patients/PatientCoursesFilters.vue'

export default {
  name: 'ParticipantCourses',
  components: {
    MainUserTable,
    PatientCoursesFilters,
  },
  mixins: [RouterMixin],
  data() {
    return {
      coursesFilters: {
        status: 'ALL',
      },
      headers: [
        { text: 'Name', value: 'title', sortable: false },
        { text: 'Rating', value: 'rating', sortable: false },
        { text: 'Started', value: 'enrolledDate', sortable: false },
        { text: 'Steps', value: 'stepsCompleted', sortable: false },
        { text: 'Status', value: 'done', sortable: false },
        { sortable: false },
      ],
    }
  },
  computed: {
    ...mapState('dictionariesStates', ['dictionaryPracticeUserRole']),
    ...mapState('patientsStates', [
      'currentPatient',
      'courses',
      'pagination',
      'loading',
    ]),
    ...mapGetters('userStates', ['getRolename']),
    isCurrentPageRootLevel() {
      return this.$route.meta.root
    },
    currentParticipantId() {
      return this.$route.params?.id || this.currentPatient?.id || null
    },
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
    coursesWithCompletePercentage() {
      return this.courses.map(el => {
        const allSteps = el.steps || 0
        const completedSteps = el.stepsCompleted || 0
        const completePercentage =
          allSteps === 0
            ? '0.00'
            : ((completedSteps / allSteps) * 100).toFixed(2)
        return {
          ...el,
          completePercentage,
        }
      })
    },
  },
  mounted() {
    this.init()
  },
  created() {},
  watch: {
    // commented out because rerender the component and call init method multiple times
    // currentPatient(patient) {
    //   if (patient?.id) this.init()
    // },
    $route(to, from) {
      // get new current patient data when patient id changed
      if (to.params?.id !== undefined && from.params?.id !== to.params?.id) {
        this.getPatient(to.params.id)
      }

      if (to.params?.status !== from.params?.status) {
        this.coursesFilters.status = from.params?.status
        this.getParticipantCourses({
          participantId: this.currentParticipantId,
          status: this.coursesFilters.status,
        })
      }
    },
  },
  methods: {
    ...mapActions('patientsStates', [
      'getParticipantCourses',
      'getPatient',
      'unassignParticipantFromCourse',
    ]),
    ...mapMutations('patientsStates', ['setCurrentCourse']),
    init() {
      if (!this.isCurrentPageRootLevel) {
        this.getParticipantCourses({
          participantId: this.currentParticipantId,
          status: this.coursesFilters.status,
        })
      }
    },
    navigateToCourses() {
      if (!this.isAdvisor) return
      this.$router.push({
        name: 'Courses',
        path: '/courses',
        query: {
          status: this.coursesFilters.status,
        },
      })
    },
    navigateToCourse(id) {
      if (id && this.currentParticipantId) {
        const course =
          this.coursesWithCompletePercentage.find(el => el.id === id) || null
        this.setCurrentCourse(course)

        this.$router.push({
          name: 'Patient Course',
          path: '/participants/:id/courses/:courseid',
          params: {
            id: this.currentParticipantId,
            courseid: id,
            status: this.coursesFilters.status,
          },
        })
      }
    },
    filterCourses(val) {
      this.coursesFilters.status = val
      this.getParticipantCourses({
        participantId: this.currentParticipantId,
        status: val,
      })
    },
    unassignCourse(id) {
      this.unassignParticipantFromCourse({
        participantId: this.currentParticipantId,
        courseId: id,
        status: this.coursesFilters.status,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.participant-courses {
  flex: 1;
  padding: 24px 10px 0;
  height: 100%;
  h3 {
    color: $bh-gray;
    margin-bottom: 10px;
    padding-right: 80px;
    small {
      margin-left: 8px;
      font-size: 0.6em;
      font-weight: 400;
    }
  }
  .v-chip-group {
    margin-bottom: 30px;
    .v-slide-group__content {
      background: $bh-gray--6;
      flex: 0;
      padding-left: 8px;
      border-radius: 20px;
    }
  }
  h2 {
    color: $bh-gray--2;
    font-size: 14px;
  }
  &--wrap {
    height: 100%;
  }
  &--not-found {
    h2 {
      margin-top: 30px;
    }
  }
  & > .container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-bottom: 0;
    & > .row > .col {
      padding-bottom: 0;
    }
  }
  .main-user-table-empty {
    line-height: $gutter * 0.75;
  }
  .v-data-table-header th {
    padding-bottom: $gutter * 0.5 !important;
  }
}
</style>
